import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '../contexts/AuthProvider';
import Layout from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DataTable } from '@/components/ui/data-table';
import { ColumnHeader } from '@/components/ui/column-header';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Trash2, Globe, Search } from 'lucide-react';
import axiosInstance from '@/utils/axiosInstance';
import { useResellerImages } from '@/hooks/useResellerImages';
import ReactCountryFlag from 'react-country-flag';
import { getCountryFlagCode } from '../utils/countryFlags';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

/**
 * Resellers page - track reseller prices
 */
const Resellers = () => {
  const { t } = useTranslation('resellers');
  const { t: tCommon } = useTranslation('common');
  const { toast } = useToast();
  const { user } = useAuth();
  const { getResellerImage } = useResellerImages();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [approvedResellers, setApprovedResellers] = useState([]);
  const [approvedResellersLoading, setApprovedResellersLoading] = useState(true);
  const [supportedResellersFilter, setSupportedResellersFilter] = useState('');
  const [resellerInsightsFilter, setResellerInsightsFilter] = useState('');
  
  // Reseller Insights (from Dashboard)
  const [resellerInsights, setResellerInsights] = useState([]);
  const [resellerInsightsLoading, setResellerInsightsLoading] = useState(false);

  // Brand change loading state
  const [brandChangeLoading, setBrandChangeLoading] = useState(false);

  // Get active tab from URL, default to 'list'
  const activeTab = searchParams.get('tab') || 'list';

  // Fetch data on mount and when brand changes
  useEffect(() => {
    const loadData = async () => {
      setBrandChangeLoading(true);
      await Promise.all([
        fetchRequests(),
        fetchApprovedResellers(),
        fetchResellerInsights()
      ]);
      setBrandChangeLoading(false);
    };
    loadData();
  }, [user?.brand]);

  const fetchResellerInsights = async () => {
    try {
      setResellerInsightsLoading(true);
      const response = await axiosInstance.get('/api/resellers/insights/');
      setResellerInsights(response.data || []);
    } catch (err) {
      console.error('Error fetching reseller insights:', err);
    } finally {
      setResellerInsightsLoading(false);
    }
  };

  const handleResellerRowClick = (reseller) => {
    if (!reseller?.hostname) return;
    const hostname = reseller.hostname.toLowerCase();
    const normalized = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    navigate(`/products?tab=priceMonitoring&reseller=${encodeURIComponent(normalized)}`);
  };

  const fetchApprovedResellers = async () => {
    try {
      setApprovedResellersLoading(true);
      const response = await axiosInstance.get('/api/resellers/available/');
      setApprovedResellers(response.data.resellers || []);
    } catch (err) {
      console.error('Error fetching approved resellers:', err);
    } finally {
      setApprovedResellersLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setRequestsLoading(true);
      const response = await axiosInstance.get('/api/reseller-requests/');
      setRequests(response.data.results || []);
    } catch (err) {
      // Error handling
    } finally {
      setRequestsLoading(false);
    }
  };


  const columnHelper = createColumnHelper();
  const renderDiscountCell = (value) => {
    const discountValue = parseFloat(value);
    if (Number.isNaN(discountValue)) {
      return <div className="text-xs text-muted-foreground">-</div>;
    }
    if (discountValue === 0) {
      return <div className="text-xs text-muted-foreground">0%</div>;
    }
    return (
      <div className={`text-xs font-medium ${discountValue < 0 ? 'text-green-600' : 'text-red-600'}`}>
        {discountValue < 0 ? '+' : ''}{Math.abs(discountValue).toFixed(2)}%
      </div>
    );
  };
  
  // Columns for Reseller Insights table
  const resellerInsightsColumns = React.useMemo(() => [
    columnHelper.accessor('hostname', {
      id: 'hostname',
      header: ({ column }) => (
        <ColumnHeader column={column}>{t('table.reseller')}</ColumnHeader>
      ),
      cell: (info) => {
        const reseller = info.row.original;
        const favicon = getResellerImage(reseller.hostname);
        const countryCode = getCountryFlagCode(reseller.country);
        const isCom = !countryCode;

        return (
          <div className="flex items-center gap-2">
            {isCom ? (
              <Globe className="h-4 w-4" />
            ) : (
              <ReactCountryFlag
                countryCode={countryCode}
                svg
                style={{
                  width: '1em',
                  height: '1em',
                }}
              />
            )}
            {favicon && (
              <img
                src={favicon}
                alt={t('resellerFavicon')}
                className="w-4 h-4 rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <span className="font-medium text-sm">{reseller.hostname}</span>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('product_count', {
      id: 'product_count',
      meta: {
        className: 'text-center'
      },
      header: ({ column }) => (
        <ColumnHeader column={column}>{t('insights.productCount')}</ColumnHeader>
      ),
      cell: (info) => {
        const row = info.row.original;
        const productCount = info.getValue();
        const totalProducts = row.total_products || 0;
        
        return (
          <div className="text-xs">
            {totalProducts > 0 ? `${productCount} / ${totalProducts}` : productCount}
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('avg_discount', {
      id: 'avg_discount',
      meta: {
        className: 'text-right'
      },
      header: ({ column }) => (
        <ColumnHeader column={column}>{t('insights.avgDiscount')}</ColumnHeader>
      ),
      cell: (info) => renderDiscountCell(info.getValue()),
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'delta_avg_discount',
      meta: {
        className: 'text-center'
      },
      header: ({ column }) => (
        <ColumnHeader column={column}>{t('insights.deltaAvgDiscount')}</ColumnHeader>
      ),
      cell: (info) => {
        const row = info.row.original;
        const evolutionYesterday = row.evolution_yesterday;
        const evolutionLastWeek = row.evolution_last_week;
        
        const renderBadge = (evolution, label) => {
          if (evolution === null || evolution === undefined) {
            return (
              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                <span>{label}:</span>
                <span>-</span>
              </div>
            );
          }
          const value = parseFloat(evolution);
          if (Number.isNaN(value) || value === 0) {
            return (
              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                <span>{label}:</span>
                <span>0%</span>
              </div>
            );
          }
          const isDiscountIncrease = value > 0;
          const displayValue = Math.abs(value);
          const colorClass = isDiscountIncrease ? 'text-red-600 bg-red-50 dark:bg-red-950/30' : 'text-green-600 bg-green-50 dark:bg-green-950/30';
          
          return (
            <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${colorClass}`}>
              <span className="text-[9px] opacity-75">{label}:</span>
              <span>{isDiscountIncrease ? '+' : '-'}{displayValue.toFixed(2)}%</span>
            </div>
          );
        };
        
        return (
          <div className="flex flex-col items-center gap-1">
            {renderBadge(evolutionYesterday, t('insights.yesterday'))}
            {renderBadge(evolutionLastWeek, t('insights.lastWeek'))}
          </div>
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor('median_discount', {
      id: 'median_discount',
      meta: {
        className: 'text-right'
      },
      header: ({ column }) => (
        <ColumnHeader column={column}>{t('insights.medianDiscount')}</ColumnHeader>
      ),
      cell: (info) => renderDiscountCell(info.getValue()),
      enableSorting: true,
    }),
    columnHelper.accessor('min_discount', {
      id: 'min_discount',
      meta: {
        className: 'text-right'
      },
      header: ({ column }) => (
        <ColumnHeader column={column}>{t('insights.minDiscount')}</ColumnHeader>
      ),
      cell: (info) => renderDiscountCell(info.getValue()),
      enableSorting: true,
    }),
    columnHelper.accessor('max_discount', {
      id: 'max_discount',
      meta: {
        className: 'text-right'
      },
      header: ({ column }) => (
        <ColumnHeader column={column}>{t('insights.maxDiscount')}</ColumnHeader>
      ),
      cell: (info) => renderDiscountCell(info.getValue()),
      enableSorting: true,
    }),
  ], [t, getResellerImage]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axiosInstance.post('/api/reseller-requests/create/', { url });
      
      // Show success toast
      toast({
        title: t('success'),
        description: '',
      });
      
      setUrl('');
      setModalOpen(false);
      
      // Refresh requests list
      fetchRequests();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('error');
      setError(errorMessage);
      
      // Show error toast
      toast({
        title: t('error'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-500';
      case 'approved':
        return 'bg-green-500/15 text-green-600 dark:text-green-500';
      case 'rejected':
        return 'bg-red-500/15 text-red-600 dark:text-red-500';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return t('status.pending');
    return t(`status.${status.toLowerCase()}`) || status;
  };

  const handleDeleteClick = (requestId) => {
    setRequestToDelete(requestId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!requestToDelete) return;

    try {
      await axiosInstance.delete(`/api/reseller-requests/${requestToDelete}/`);
      toast({
        title: t('deleteSuccess'),
        description: '',
      });
      setDeleteConfirmOpen(false);
      setRequestToDelete(null);
      fetchRequests();
    } catch (err) {
      toast({
        title: t('deleteError'),
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    }
  };

  // Show global loader when brand changes
  if (brandChangeLoading) {
    return (
      <Layout>
        <PageHeader
          title={<h1 className="text-3xl font-bold">{t('title')}</h1>}
        />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title={<h1 className="text-3xl font-bold">{t('title')}</h1>}
        rightContent={
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('requestReseller')}
          </Button>
        }
      />
      <div className="space-y-6">
        <Tabs value={activeTab} onValueChange={(value) => {
          setSearchParams({ tab: value });
        }} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="list">{t('tabs.list')}</TabsTrigger>
            <TabsTrigger value="supported">{t('tabs.supported')}</TabsTrigger>
            <TabsTrigger value="requests">{t('tabs.requests')}</TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="space-y-6">
            {resellerInsightsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('insights.title')} ({resellerInsights.length})
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('insights.description')}
                  </p>
                </CardHeader>
                <CardContent>
                  {/* Filter input */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder={t('tabs.filterResellers')}
                        value={resellerInsightsFilter}
                        onChange={(e) => setResellerInsightsFilter(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  {(() => {
                    const filteredInsights = resellerInsights.filter(insight =>
                      insight.hostname?.toLowerCase().includes(resellerInsightsFilter.toLowerCase()) ||
                      insight.country?.toLowerCase().includes(resellerInsightsFilter.toLowerCase())
                    );

                    if (filteredInsights.length === 0) {
                      return (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                          {resellerInsights.length === 0 
                            ? t('insights.noData')
                            : t('tabs.noMatchingResellers')
                          }
                        </div>
                      );
                    }

                    return (
                      <DataTable
                        data={filteredInsights}
                        columns={resellerInsightsColumns}
                        pagination={null}
                        compact
                        onRowClick={handleResellerRowClick}
                        clickableRows
                      />
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="supported" className="space-y-6">
            {approvedResellersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('tabs.supportedTitle')} ({approvedResellers.length})
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('tabs.supportedDescription')}
                  </p>
                </CardHeader>
                <CardContent>
                  {/* Filter input */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder={t('tabs.filterSupportedResellers')}
                        value={supportedResellersFilter}
                        onChange={(e) => setSupportedResellersFilter(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  {approvedResellers.length === 0 ? (
                    <div className="py-8 text-center">
                      <p className="text-muted-foreground">{t('tabs.noSupportedResellers')}</p>
                    </div>
                  ) : (() => {
                    const filteredResellers = approvedResellers.filter(reseller =>
                      reseller.reseller.toLowerCase().includes(supportedResellersFilter.toLowerCase())
                    );

                    if (filteredResellers.length === 0) {
                      return (
                        <div className="py-8 text-center">
                          <p className="text-muted-foreground">{t('tabs.noMatchingResellers')}</p>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-2">
                        {filteredResellers.map((reseller) => (
                          <div key={reseller.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                            {reseller.image ? (
                              <img
                                src={reseller.image}
                                alt={t('resellerFavicon')}
                                className="w-5 h-5 rounded flex-shrink-0"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <Globe className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                            )}
                            <span className="font-medium text-sm flex-1">{reseller.reseller}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="requests" className="space-y-6">
            {requestsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
            ) : requests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">{t('noRequests')}</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('requests')} ({requests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b bg-muted">
                          <th className="p-3 text-left font-medium text-sm">{t('table.url')}</th>
                          <th className="p-3 text-left font-medium text-sm">{t('table.status')}</th>
                          <th className="p-3 text-left font-medium text-sm">{t('table.date')}</th>
                          <th className="p-3 text-center font-medium text-sm">{t('table.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((request, index) => (
                          <tr key={index} className="border-b hover:bg-muted/50">
                            <td className="p-3">
                              <a
                                href={request.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:text-primary/80"
                              >
                                {request.url}
                              </a>
                            </td>
                            <td className="p-3">
                              <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(request.status)}`}>
                                {getStatusLabel(request.status)}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className="text-sm text-muted-foreground">
                                {new Date(request.created_at).toLocaleDateString()}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(request.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
      </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('modalTitle')}</DialogTitle>
            <DialogDescription>
              {t('modalDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <form id="reseller-form" onSubmit={handleSubmit} className="space-y-4 px-6">
            <div>
              <label htmlFor="url" className="text-sm font-medium mb-2 block">
                {t('resellerUrl')}
              </label>
              <Input
                id="url"
                type="text"
                placeholder={t('urlExample')}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                className="w-full"
              />
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm p-3 rounded-md">
                {error}
              </div>
            )}
          </form>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setModalOpen(false);
                  setUrl('');
                  setError('');
                }}
              >
              {tCommon('cancel')}
              </Button>
              <Button
                type="submit"
              form="reseller-form"
                disabled={loading}
              >
              {loading ? t('submitting') : t('submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('confirmDeleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('confirmDeleteDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setRequestToDelete(null);
              }}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
            >
              {t('confirmDelete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
};

export default Resellers;